import { createConfig, createStorage, http } from 'wagmi';
import { mainnet, sepolia } from 'wagmi/chains';
import { injected, metaMask } from 'wagmi/connectors';
import { defineChain } from 'viem';
import { config as walletConfig } from 'src/config-global';
import { openMetaMaskLink } from 'src/utils/metaMaskOpenLink';

const VALID_TARGETS = ['sepolia', 'mainnet', 'localhost'];
const IS_DEVELOPMENT = Boolean(walletConfig?.isDevelopment ?? import.meta.env.DEV);

const envTarget = import.meta.env.VITE_RPC_TARGET;

// ── Config validation ─────────────────────────────────────────────────────
// Previously this threw at module import, which white-screened the ENTIRE app
// on a misconfigured env — including for users who never touch a wallet.
//
// It also must NOT silently fall back to a different chain: a prod build that
// quietly ran on sepolia while users believed they were on mainnet is worse
// than a crash. So instead: record the error, build a structurally valid
// config so WagmiProvider still mounts, and let the UI refuse to connect.
let configError = null;

if (!envTarget || !VALID_TARGETS.includes(envTarget)) {
  configError = `Invalid VITE_RPC_TARGET: ${envTarget}`;
  console.error(`[wagmi] ${configError}`);
}

const resolvedTarget = configError ? 'sepolia' : envTarget;

export const TARGET = resolvedTarget;
export const LOCAL_RPC_URL = import.meta.env.VITE_LOCAL_RPC_URL || 'http://127.0.0.1:8545';

export const hardhat = defineChain({
  id: 31337,
  name: 'Hardhat',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: {
    default: { http: [LOCAL_RPC_URL] },
    public: { http: [LOCAL_RPC_URL] },
  },
});

const rpcUrls = {
  sepolia: import.meta.env.VITE_DRPC_SEPOLIA_RPC_URL,
  mainnet: import.meta.env.VITE_DRPC_MAIN_RPC_URL,
  localhost: LOCAL_RPC_URL,
};

// On a phone browser the MetaMask connector sends gas estimates, nonces and
// balance reads to the chain's DEFAULT rpc url (a free public node), not to
// our transports. Those nodes rate-limit, which made "Send" fail on mobile.
// Pointing the default url at dRPC keeps those reads on our own endpoint.
function withRpcUrl(chain, url) {
  if (!url) return chain;

  return {
    ...chain,
    rpcUrls: {
      ...chain.rpcUrls,
      default: { ...chain.rpcUrls.default, http: [url] },
    },
  };
}

const sepoliaChain = withRpcUrl(sepolia, rpcUrls.sepolia);
const mainnetChain = withRpcUrl(mainnet, rpcUrls.mainnet);

const chainsByTarget = {
  sepolia: sepoliaChain,
  mainnet: mainnetChain,
  localhost: hardhat,
};

if (!configError && TARGET !== 'localhost' && !rpcUrls[TARGET]) {
  configError =
    `Missing dRPC URL for target "${TARGET}". ` +
    `Set VITE_DRPC_${TARGET.toUpperCase()}_RPC_URL in your .env file.`;
  console.error(`[wagmi] ${configError}`);
}

// Non-null only when the wallet layer must refuse to operate. The connect
// button reads this and disables itself rather than transacting on a chain
// the app was not configured for.
export const WALLET_CONFIG_ERROR = configError;

export const TARGET_CHAIN = chainsByTarget[TARGET];
export const TARGET_CHAIN_ID = TARGET_CHAIN.id;

const EXTRA_CHAINS = [sepoliaChain, mainnetChain, hardhat].filter(
  (chain) => chain.id !== TARGET_CHAIN_ID
);

export const chains = [TARGET_CHAIN, ...EXTRA_CHAINS];

const isBrowser = typeof window !== 'undefined';

// MetaMask mobile's in-app browser already injects its provider as
// window.ethereum. The MetaMask SDK connector does not need to (and on some
// app versions does not reliably) run its deep-link / relay connect flow
// there, which left "Connect" spinning until the 15s timeout. Inside the
// MetaMask app we connect straight to the injected provider instead.
function detectMetaMaskInAppBrowser() {
  if (!isBrowser) return false;

  try {
    const ua = window.navigator?.userAgent || '';

    if (/MetaMaskMobile/i.test(ua)) return true;

    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
    return isMobile && Boolean(window.ethereum?.isMetaMask);
  } catch {
    return false;
  }
}

export const IS_METAMASK_IN_APP = detectMetaMaskInAppBrowser();

function detectMobileBrowser() {
  if (!isBrowser) return false;

  try {
    return /Android|iPhone|iPad|iPod/i.test(window.navigator?.userAgent || '');
  } catch {
    return false;
  }
}

// Chrome, Safari, Samsung Internet, etc. on a phone: connects to the MetaMask
// app through the relay and has to switch apps for every approval.
export const IS_MOBILE_WEB = detectMobileBrowser() && !IS_METAMASK_IN_APP;

const dapp = {
  name: walletConfig?.appName || 'SafiSwap',
  url: 'https://safiswap.com',
  iconUrl: 'https://safiswap.com/favicon.ico',
};

// dRPC-only transport. batch is disabled because the load-balanced dRPC
// endpoint was dropping batched JSON-RPC arrays (network tab showed provisional
// headers / no response), taking down whole groups of balance/allowance reads
// at once. retryCount is kept low and retryDelay generous because there is no
// fallback provider — if dRPC is rate-limiting, aggressive retries make it
// worse, so this errs on the side of backing off rather than hammering.
function mkDrpcTransport(url, timeout = 10_000) {
  if (!url) {
    throw new Error(`Missing dRPC URL for "${TARGET}". Check your .env file.`);
  }

  return http(url, {
    batch: false,
    retryCount: 1,
    retryDelay: 500,
    timeout,
  });
}

function rpcUrlForChain(chainId) {
  if (chainId === hardhat.id) return LOCAL_RPC_URL;
  if (chainId === sepolia.id) return rpcUrls.sepolia;
  if (chainId === mainnet.id) return rpcUrls.mainnet;

  return null;
}

// ── Transports for EVERY chain in `chains`, not just the target ───────────
// switchChain targets the extra chains, and wrong-network detection reads from
// them. With no transport defined, viem falls back to the chain's default
// public RPC — rate-limited and unreliable. Configured URLs use the dRPC
// transport; anything without one falls back to http() explicitly so the
// behaviour is visible here rather than implicit.
function buildTransports() {
  const entries = {};

  chains.forEach((chain) => {
    const url = rpcUrlForChain(chain.id);

    if (url) {
      let timeout = 10_000;

      if (chain.id === hardhat.id) {
        timeout = 5_000;
      }

      entries[chain.id] = mkDrpcTransport(url, timeout);
    } else {
      console.warn(`[wagmi] No configured RPC for chain ${chain.id} — using public fallback.`);
      entries[chain.id] = http();
    }
  });

  return entries;
}

export const transports = buildTransports();

// In the MetaMask app: the injected provider only. Everywhere else: the
// MetaMask SDK (extension on desktop, deep link into the app on mobile).
function buildConnectors() {
  if (IS_METAMASK_IN_APP) {
    // shimDisconnect off: with it on, connect first calls
    // wallet_requestPermissions, which MetaMask mobile can leave open without
    // showing a prompt, so every later attempt fails with "request already
    // open". Plain eth_requestAccounts is what the MetaMask app expects.
    return [injected({ target: 'metaMask', shimDisconnect: false })];
  }

  return [
    metaMask({
      dapp,
      debug: IS_DEVELOPMENT,
      // Phone browsers block opening another app once a few seconds have passed
      // since the tap, and the connector only has the link after it has set up
      // the relay. We take the link, try to open it, and keep it for the
      // "Open MetaMask" button (see utils/metaMaskOpenLink.js).
      mobile: {
        preferredOpenLink: openMetaMaskLink,
      },
    }),
  ];
}

function getSafeStorage() {
  if (!isBrowser) return undefined;

  try {
    if (window.localStorage) {
      return createStorage({ storage: window.localStorage });
    }

    return undefined;
  } catch {
    return undefined;
  }
}

// multiInjectedProviderDiscovery: false prevents wagmi from registering
// multiple injected provider instances when MetaMask mobile's WebView
// re-injects window.ethereum after a background/foreground cycle. Multiple
// instances cause duplicate connectors and can route writes to the wrong
// provider, compounding the channel-routing confusion fixed in useEthereum.js.
//
// TRADE-OFF: with discovery off and metaMask() as the only connector, users on
// Rabby / Coinbase Wallet / Brave cannot connect at all. Keep this only if
// MetaMask-only is an intentional product decision.
export const config = createConfig({
  chains,
  connectors: buildConnectors(),
  transports,
  storage: getSafeStorage(),
  ssr: false,
  syncConnectedChain: true,
  multiInjectedProviderDiscovery: false,
  // Real-time updates arrive over the socket, so aggressive chain polling
  // mostly burns dRPC quota (and batch is disabled, so each watch is its own
  // request). Raised from 8s.
  pollingInterval: 20_000,
});
